/**
 * Apresentação «Catálogo luxo» — alternativa exclusiva à vitrine modular (`storeHomeLayout`).
 * Configuração em `tenant.theme_json.storeCatalogLuxe`.
 */

export const STORE_CATALOG_LUXE_EDITOR_EXAMPLE = {
  hero: {
    badge: 'Edição especial',
    titleBefore: 'Presentes que',
    titleAccent: 'falam de amor',
    subtitle:
      'Cestas, buquês e caixas feitas à mão. Cada detalhe pensado para transformar momentos em memórias inesquecíveis.',
    ctaCatalog: 'Ver o catálogo',
    image_url: '',
  },
  marquee: {
    items: ['Feito à mão', 'Entrega com carinho', 'Acabamento premium', 'Personalização'],
  },
  story: {
    kicker: 'Nossa essência',
    title: 'O amor está nos detalhes',
    titleAccent: 'e nas escolhas que fazem o coração sorrir.',
  },
  catalog: {
    kicker: 'Catálogo',
    titleBefore: 'Surpresas que',
    titleAccent: 'emocionam',
  },
  howItWorks: {
    enabled: true,
    kicker: 'Como funciona',
    titleBefore: 'Simples, rápido e',
    titleAccent: 'cheio de carinho',
    steps: [
      { title: 'Escolha os produtos', text: 'Navegue pelo catálogo e monte o seu pedido.' },
      { title: 'Finalize na loja', text: 'Informe entrega, pagamento e observações.' },
      { title: 'Confirmação', text: 'A loja recebe o pedido e entra em contacto se precisar.' },
      { title: 'Entrega', text: 'Receba com todo o cuidado que o momento merece.' },
    ],
  },
  customBlock: {
    enabled: true,
    kicker: 'Sob medida',
    title: 'Monte do seu jeito',
    subtitle: 'Fale connosco para personalizar cestas, mensagens e combinações exclusivas.',
    ctaLabel: 'Falar com a loja',
  },
};

/** @param {unknown} themeJson */
export function normalizeThemeJson(themeJson) {
  let obj = themeJson;
  if (typeof obj === 'string') {
    try {
      obj = JSON.parse(obj);
    } catch {
      return {};
    }
  }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return {};
  return { ...obj };
}

/**
 * @param {unknown} themeJson
 * @returns {{
 *   enabled: boolean,
 *   hero: Record<string, string>,
 *   marquee: { items: string[] },
 *   story: Record<string, string>,
 *   catalog: Record<string, string>,
 *   howItWorks: { enabled: boolean, kicker: string, titleBefore: string, titleAccent: string, steps: { title: string, text: string }[] },
 *   customBlock: { enabled: boolean, kicker: string, title: string, subtitle: string, ctaLabel: string },
 * }}
 */
export function parseStoreCatalogLuxe(themeJson) {
  const base = normalizeThemeJson(themeJson);
  const raw = base.storeCatalogLuxe;
  const ex = STORE_CATALOG_LUXE_EDITOR_EXAMPLE;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { enabled: false, ...mergeCatalogLuxeDefaults(ex) };
  }
  const enabled = Boolean(raw.enabled);
  return {
    enabled,
    ...mergeCatalogLuxeDefaults(raw),
  };
}

/** @param {Record<string, unknown>} raw */
function mergeCatalogLuxeDefaults(raw) {
  const ex = STORE_CATALOG_LUXE_EDITOR_EXAMPLE;
  const heroRaw = raw.hero && typeof raw.hero === 'object' && !Array.isArray(raw.hero) ? raw.hero : {};
  const hero = { ...ex.hero };
  for (const k of Object.keys(ex.hero)) {
    if (typeof heroRaw[k] === 'string') hero[k] = heroRaw[k];
  }

  const marqueeRaw = raw.marquee && typeof raw.marquee === 'object' ? raw.marquee : {};
  const items = Array.isArray(marqueeRaw.items)
    ? marqueeRaw.items.map((x) => String(x).trim()).filter(Boolean)
    : ex.marquee.items;

  const storyRaw = raw.story && typeof raw.story === 'object' ? raw.story : {};
  const story = { ...ex.story };
  for (const k of Object.keys(ex.story)) {
    if (typeof storyRaw[k] === 'string') story[k] = storyRaw[k];
  }

  const catalogRaw = raw.catalog && typeof raw.catalog === 'object' ? raw.catalog : {};
  const catalog = { ...ex.catalog };
  for (const k of Object.keys(ex.catalog)) {
    if (typeof catalogRaw[k] === 'string') catalog[k] = catalogRaw[k];
  }

  const hiwRaw = raw.howItWorks && typeof raw.howItWorks === 'object' ? raw.howItWorks : {};
  const steps = Array.isArray(hiwRaw.steps)
    ? hiwRaw.steps
        .filter((s) => s && typeof s === 'object')
        .map((s) => ({
          title: String(s.title || '').trim() || 'Passo',
          text: String(s.text || '').trim(),
        }))
    : ex.howItWorks.steps;
  const howItWorks = {
    enabled: hiwRaw.enabled !== false,
    kicker: typeof hiwRaw.kicker === 'string' ? hiwRaw.kicker : ex.howItWorks.kicker,
    titleBefore: typeof hiwRaw.titleBefore === 'string' ? hiwRaw.titleBefore : ex.howItWorks.titleBefore,
    titleAccent: typeof hiwRaw.titleAccent === 'string' ? hiwRaw.titleAccent : ex.howItWorks.titleAccent,
    steps: steps.length ? steps : ex.howItWorks.steps,
  };

  const customRaw = raw.customBlock && typeof raw.customBlock === 'object' ? raw.customBlock : {};
  const customBlock = {
    enabled: customRaw.enabled !== false,
    kicker: typeof customRaw.kicker === 'string' ? customRaw.kicker : ex.customBlock.kicker,
    title: typeof customRaw.title === 'string' ? customRaw.title : ex.customBlock.title,
    subtitle: typeof customRaw.subtitle === 'string' ? customRaw.subtitle : ex.customBlock.subtitle,
    ctaLabel: typeof customRaw.ctaLabel === 'string' ? customRaw.ctaLabel : ex.customBlock.ctaLabel,
  };

  return { hero, marquee: { items }, story, catalog, howItWorks, customBlock };
}

/** Corpo para gravar em theme_json (sem `enabled` — controlado no formulário). */
export function storeCatalogLuxeBodyForEditor(themeJson) {
  const parsed = parseStoreCatalogLuxe(themeJson);
  return {
    hero: parsed.hero,
    marquee: parsed.marquee,
    story: parsed.story,
    catalog: parsed.catalog,
    howItWorks: parsed.howItWorks,
    customBlock: parsed.customBlock,
  };
}
